<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { messageStamp } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';

const props = defineProps({
  automation: {
    type: Object,
    required: true,
  },
  folders: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle', 'edit', 'delete', 'clone', 'move']);
const { t } = useI18n();
const showMoveMenu = ref(false);

const readableDate = date => messageStamp(new Date(date), 'LLL d, yyyy');
const readableDateWithTime = date =>
  messageStamp(new Date(date), 'LLL d, yyyy hh:mm a');

const moveItems = computed(() => {
  const currentId = props.automation.automation_folder_id;
  const items = props.folders
    .filter(folder => folder.id !== currentId)
    .map(folder => ({
      label:
        folder.name.length > 20
          ? `${folder.name.slice(0, 20)}...`
          : folder.name,
      action: 'move',
      value: String(folder.id),
      icon: 'i-lucide-folder',
    }));

  if (currentId) {
    items.unshift({
      label: t('AUTOMATION.FOLDERS.NONE'),
      action: 'move',
      value: 'none',
      icon: 'i-lucide-folder-x',
    });
  }

  return items;
});

const onMove = item => {
  showMoveMenu.value = false;
  emit('move', {
    id: props.automation.id,
    automation_folder_id: item.value === 'none' ? null : Number(item.value),
  });
};

const automationActive = computed({
  get: () => props.automation.active,
  set: active => {
    const { id, name } = props.automation;
    emit('toggle', {
      id,
      name,
      status: !active,
    });
  },
});
</script>

<template>
  <BaseTableRow :item="automation">
    <template #default>
      <BaseTableCell class="whitespace-normal">
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span class="text-body-main text-n-slate-12 break-words">
            {{ automation.name }}
          </span>
          <div class="w-px h-3 rounded-lg bg-n-weak flex-shrink-0" />
          <span class="text-body-main text-n-slate-11 break-words">
            {{ automation.description }}
          </span>
        </div>
      </BaseTableCell>

      <BaseTableCell>
        <ToggleSwitch v-model="automationActive" />
      </BaseTableCell>

      <BaseTableCell :title="readableDateWithTime(automation.created_on)">
        <span class="text-body-main text-n-slate-12 whitespace-nowrap">
          {{ readableDate(automation.created_on) }}
        </span>
      </BaseTableCell>

      <BaseTableCell align="end">
        <div class="flex gap-3 justify-end flex-shrink-0">
          <div
            v-if="folders.length"
            v-on-clickaway="() => (showMoveMenu = false)"
            class="relative"
          >
            <Button
              v-tooltip.top="$t('AUTOMATION.FOLDERS.MOVE')"
              icon="i-lucide-folder-input"
              slate
              sm
              @click="showMoveMenu = !showMoveMenu"
            />
            <DropdownMenu
              v-if="showMoveMenu"
              :menu-items="moveItems"
              class="ltr:right-0 rtl:left-0 top-full mt-1 w-52 max-h-64 overflow-y-auto"
              @action="onMove"
            />
          </div>
          <Button
            v-tooltip.top="$t('AUTOMATION.FORM.EDIT')"
            icon="i-woot-edit-pen"
            slate
            sm
            :is-loading="loading"
            @click="$emit('edit', automation)"
          />
          <Button
            v-tooltip.top="$t('AUTOMATION.CLONE.TOOLTIP')"
            icon="i-woot-clone"
            sm
            slate
            :is-loading="loading"
            @click="$emit('clone', automation)"
          />
          <Button
            v-tooltip.top="$t('AUTOMATION.FORM.DELETE')"
            :is-loading="loading"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="$emit('delete', automation)"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
